import { c as _c } from "react/compiler-runtime";
import { translateInline } from '@/globals/translate/translateInline';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { stack } from '@/uiPrimitives/layout/stack';
import { AnswerContainer, AnswerLabel, Form, Radio, SecondaryButton, StackAnswers } from '../decisionToolArticle/_questions/currentQuestion/CurrentQuestionParts';
import { QuestionHelp } from '../decisionToolArticle/_questions/currentQuestion/Help';
import { QuestionTranslation, questionTranslations } from './legalForm.config';
import { useEffect, useId, useMemo, useRef } from 'react';
import { useGivenAnswer, useGivenAnswers } from './legalFormHooks';
import { textBlock } from '@/uiPrimitives/typography/text';
import { disabledButton, hiddenUnlessUserInvalid } from '../decisionToolArticle/DecisionTool.css';
import { callToActionBox } from '@/uiComposites/interactive/callToAction';
import { h3TextBlock } from '@/uiComposites/typography/headings';
import { rows } from '@/uiPrimitives/layout/rows';
import { FieldIssue } from '../contactOverview/[contactOverview]/contactForm/[contactForm]/FieldIssue';
import { hidden, hiddenAccessible } from '@/uiPrimitives/utility/display.css';
import { box } from '@/uiPrimitives/layout/box';
import { shelf } from '@/uiPrimitives/layout/shelf';
import { useQuestionIndexContext } from './useQuestionIndex';
import { overflowClip } from '@/uiPrimitives/utility/overflow.css';
import { addTestSelector } from '@/globals/testHelpers/testSelector';
import { useLegalFormAnalytics } from './LegalFormAnalyticsProvider';
import { pushLegalFormSubmitEvents } from './pushLegalFormSubmitEvents';
import { pushPreviousStepEvent } from '@/globals/analytics/events/pushDecisionToolEvent';
const FIRST_QUESTION_ID = 'at';
export const useIsPlural = () => {
  const $ = _c(2);
  const givenAnswers = useGivenAnswers();
  let t0;
  if ($[0] !== givenAnswers) {
    t0 = givenAnswers.get(FIRST_QUESTION_ID);
    $[0] = givenAnswers;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const firstQuestionAnswer = t0;
  return firstQuestionAnswer !== "a";
};
export const CurrentQuestion = t0 => {
  const $ = _c(126);
  const {
    question,
    id
  } = t0;
  const ref = useRef(null);
  const [questionIndexState, setQuestionIndex] = useQuestionIndexContext();
  const errorId = useId();
  const [givenAnswer, setGivenAnswer] = useGivenAnswer(question.id);
  const givenAnswers = useGivenAnswers();
  const {
    hasStartedDecisionTool,
    hasViewedResult,
    onDecisionToolStarted,
    onResultViewed
  } = useLegalFormAnalytics();
  const isPlural = useIsPlural();
  const allQuestionsAnswered = Array.from(givenAnswers.values()).every(_temp);
  let t1;
  let t2;
  if ($[0] !== id || $[1] !== questionIndexState.cause) {
    t1 = () => {
      if (ref.current && questionIndexState.cause !== "initial") {
        const rect = ref.current.getBoundingClientRect();
        const isInView = rect.top >= 0 && rect.bottom <= window.innerHeight;
        if (!isInView) {
          ref.current.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
        }
        const currentQuestion = document.getElementById(id);
        if (currentQuestion != null) {
          currentQuestion.focus({
            preventScroll: true
          });
        }
      }
    };
    t2 = [id, questionIndexState.cause];
    $[0] = id;
    $[1] = questionIndexState.cause;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  if (questionIndexState.index === -1) {
    return null;
  }
  const firstUnansweredQuestion = questionIndexState.index === 0 && givenAnswer == null;
  const previousQuestion = questionTranslations[questionIndexState.index - 1];
  const questionsRemaining = questionTranslations.length - questionIndexState.index - 1;
  let t3;
  if ($[4] !== givenAnswer || $[5] !== givenAnswers || $[6] !== hasStartedDecisionTool || $[7] !== hasViewedResult || $[8] !== isPlural || $[9] !== onDecisionToolStarted || $[10] !== onResultViewed || $[11] !== question || $[12] !== setQuestionIndex) {
    t3 = event => {
      event.preventDefault();
      const form = event.currentTarget;
      if (!form.checkValidity()) {
        return;
      }
      const nextQuestionIndex = [...givenAnswers.values()].findIndex(_temp2);
      const formData = new FormData(form);
      const currentAnswer = formData.get(question.id);
      const submittedAnswerId = typeof currentAnswer === "string" ? currentAnswer : !question.required && givenAnswer == null ? "" : null;
      const nextGivenAnswers = new Map(givenAnswers);
      nextGivenAnswers.set(question.id, submittedAnswerId);
      const isCompletingDecisionTool = nextQuestionIndex === -1;
      pushLegalFormSubmitEvents({
        givenAnswers: nextGivenAnswers,
        hasStartedDecisionTool,
        hasViewedResult,
        isCompletingDecisionTool,
        isPlural,
        onDecisionToolStarted,
        onResultViewed,
        question,
        submittedAnswerId
      });
      setQuestionIndex({
        index: nextQuestionIndex,
        cause: "answer"
      });
    };
    $[4] = givenAnswer;
    $[5] = givenAnswers;
    $[6] = hasStartedDecisionTool;
    $[7] = hasViewedResult;
    $[8] = isPlural;
    $[9] = onDecisionToolStarted;
    $[10] = onResultViewed;
    $[11] = question;
    $[12] = setQuestionIndex;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  let t5;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = classes(stack({
      rowGap: "3 | h1"
    }));
    t5 = classes(textBlock({
      capSize: "-1 | small"
    }));
    $[14] = t4;
    $[15] = t5;
  } else {
    t4 = $[14];
    t5 = $[15];
  }
  let t6;
  if ($[16] !== questionsRemaining) {
    t6 = questionsRemaining > 0 ? translateInline({
      en: `${questionsRemaining} more question${questionsRemaining === 1 ? "" : "s"} to go`,
      nl: `Nog ${questionsRemaining} ${questionsRemaining === 1 ? "vraag" : "vragen"}`
    }) : translateInline({
      en: "Last question",
      nl: "Laatste vraag"
    });
    $[16] = questionsRemaining;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t5 || $[19] !== t6) {
    t7 = <div className={t5}>{t6}</div>;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t10;
  let t8;
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = classes(stack({
      rowGap: "2 | h2"
    }));
    t9 = classes(box({
      backgroundColor: "negative"
    }), overflowClip);
    t10 = classes(hiddenAccessible);
    $[21] = t10;
    $[22] = t8;
    $[23] = t9;
  } else {
    t10 = $[21];
    t8 = $[22];
    t9 = $[23];
  }
  const t11 = 5 - questionsRemaining;
  let t12;
  if ($[24] !== questionsRemaining) {
    t12 = questionsRemaining >= 1 ? translateInline({
      en: `${questionsRemaining} ${questionsRemaining === 1 ? "question" : "questions"} to go`,
      nl: `Nog ${questionsRemaining} ${questionsRemaining === 1 ? "vraag" : "vragen"} te gaan`
    }) : translateInline({
      en: "This is the last question",
      nl: "Dit is de laatste vraag"
    });
    $[24] = questionsRemaining;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t11 || $[27] !== t12) {
    t13 = <progress className={t10} max={5} value={t11} aria-label={t12} />;
    $[26] = t11;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = classes(box({
      backgroundColor: "positiveBlue",
      blockSize: "-2 | smaller"
    }));
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const t15 = `${(5 - questionsRemaining) / 5 * 100}%`;
  let t16;
  if ($[30] !== t15) {
    t16 = {
      inlineSize: t15
    };
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t14 || $[33] !== t16) {
    t17 = <i className={t14} style={t16} />;
    $[32] = t14;
    $[33] = t16;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== t13 || $[36] !== t17 || $[37] !== t9) {
    t18 = <div className={t9}>{t13}{t17}</div>;
    $[35] = t13;
    $[36] = t17;
    $[37] = t9;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== t18 || $[40] !== t8) {
    t19 = <div className={t8}>{t18}</div>;
    $[39] = t18;
    $[40] = t8;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = classes(stack({
      rowGap: "5 | betweenParagraphs"
    }));
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  const t21 = question.help;
  const t22 = question.title;
  const t23 = question.id;
  const t24 = `uiQuestion-${questionIndexState.index + 1}`;
  let t25;
  if ($[43] !== t24) {
    t25 = addTestSelector(t24);
    $[43] = t24;
    $[44] = t25;
  } else {
    t25 = $[44];
  }
  let t26;
  let t27;
  let t28;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = classes(rows({
      columnGap: "-2 | smaller",
      rowGap: "1 | h3",
      alignItems: "baseline"
    }));
    t27 = classes(textBlock({
      capSize: "1 | h3"
    }));
    t28 = translateInline({
      en: "Question",
      nl: "Vraag"
    });
    $[45] = t26;
    $[46] = t27;
    $[47] = t28;
  } else {
    t26 = $[45];
    t27 = $[46];
    t28 = $[47];
  }
  const t29 = questionIndexState.index + 1;
  let t30;
  if ($[48] !== t27 || $[49] !== t28 || $[50] !== t29) {
    t30 = <span className={t27}>{t28}{" "}{t29}{":"}</span>;
    $[48] = t27;
    $[49] = t28;
    $[50] = t29;
    $[51] = t30;
  } else {
    t30 = $[51];
  }
  let t31;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = classes(h3TextBlock({}));
    $[52] = t31;
  } else {
    t31 = $[52];
  }
  let t32;
  if ($[53] !== question.title || $[54] !== t31) {
    t32 = <span className={t31}>{question.title}</span>;
    $[53] = question.title;
    $[54] = t31;
    $[55] = t32;
  } else {
    t32 = $[55];
  }
  let t33;
  if ($[56] !== t26 || $[57] !== t30 || $[58] !== t32) {
    t33 = <div className={t26}>{t30}{t32}</div>;
    $[56] = t26;
    $[57] = t30;
    $[58] = t32;
    $[59] = t33;
  } else {
    t33 = $[59];
  }
  let t34;
  if ($[60] !== id || $[61] !== t25 || $[62] !== t33) {
    t34 = <h2 {...t25} id={id} tabIndex={-1}>{t33}</h2>;
    $[60] = id;
    $[61] = t25;
    $[62] = t33;
    $[63] = t34;
  } else {
    t34 = $[63];
  }
  let t35;
  if ($[64] !== question.help || $[65] !== question.id || $[66] !== question.title || $[67] !== t34) {
    t35 = <QuestionHelp help={t21} titleText={t22} questionId={t23}>{t34}</QuestionHelp>;
    $[64] = question.help;
    $[65] = question.id;
    $[66] = question.title;
    $[67] = t34;
    $[68] = t35;
  } else {
    t35 = $[68];
  }
  let t36;
  if ($[69] !== errorId || $[70] !== givenAnswer || $[71] !== isPlural || $[72] !== question.answers || $[73] !== question.id || $[74] !== question.required || $[75] !== setGivenAnswer) {
    let t37;
    if ($[77] !== errorId || $[78] !== givenAnswer || $[79] !== isPlural || $[80] !== question.id || $[81] !== question.required || $[82] !== setGivenAnswer) {
      t37 = answer_0 => <AnswerContainer key={answer_0.id}><Radio name={question.id} value={answer_0.id} checked={givenAnswer === answer_0.id} onChange={() => {
          setGivenAnswer(answer_0.id);
        }} aria-describedby={errorId} required={question.required} {...addTestSelector(`uiAnswer-${answer_0.id}`)} /><AnswerLabel>{isPlural && answer_0.text.plural ? answer_0.text.plural : answer_0.text.single}</AnswerLabel></AnswerContainer>;
      $[77] = errorId;
      $[78] = givenAnswer;
      $[79] = isPlural;
      $[80] = question.id;
      $[81] = question.required;
      $[82] = setGivenAnswer;
      $[83] = t37;
    } else {
      t37 = $[83];
    }
    t36 = question.answers.map(t37);
    $[69] = errorId;
    $[70] = givenAnswer;
    $[71] = isPlural;
    $[72] = question.answers;
    $[73] = question.id;
    $[74] = question.required;
    $[75] = setGivenAnswer;
    $[76] = t36;
  } else {
    t36 = $[76];
  }
  let t37;
  if ($[84] !== t36) {
    t37 = <StackAnswers>{t36}</StackAnswers>;
    $[84] = t36;
    $[85] = t37;
  } else {
    t37 = $[85];
  }
  let t38;
  if ($[86] !== t20 || $[87] !== t35 || $[88] !== t37) {
    t38 = <div role="radiogroup" className={t20}>{t35}{t37}</div>;
    $[86] = t20;
    $[87] = t35;
    $[88] = t37;
    $[89] = t38;
  } else {
    t38 = $[89];
  }
  let t39;
  if ($[90] !== firstUnansweredQuestion) {
    t39 = firstUnansweredQuestion ? classes(hiddenUnlessUserInvalid, box({
      paddingBlockStart: "4 | betweenListItems"
    })) : hidden;
    $[90] = firstUnansweredQuestion;
    $[91] = t39;
  } else {
    t39 = $[91];
  }
  let t40;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = translateInline({
      en: "You have not selected an answer yet. Please choose an option to proceed.",
      nl: "U heeft nog geen antwoord geselecteerd. Kies een optie om verder te gaan."
    });
    $[92] = t40;
  } else {
    t40 = $[92];
  }
  let t41;
  if ($[93] !== errorId || $[94] !== t39 || $[95] !== t40) {
    t41 = <FieldIssue id={errorId} hiddenClassName={t39}>{t40}</FieldIssue>;
    $[93] = errorId;
    $[94] = t39;
    $[95] = t40;
    $[96] = t41;
  } else {
    t41 = $[96];
  }
  let t42;
  if ($[97] !== t38 || $[98] !== t41) {
    t42 = <div>{t38}{t41}</div>;
    $[97] = t38;
    $[98] = t41;
    $[99] = t42;
  } else {
    t42 = $[99];
  }
  let t43;
  if ($[100] !== t19 || $[101] !== t4 || $[102] !== t42 || $[103] !== t7) {
    t43 = <div ref={ref} className={t4}>{t7}{t19}{t42}</div>;
    $[100] = t19;
    $[101] = t4;
    $[102] = t42;
    $[103] = t7;
    $[104] = t43;
  } else {
    t43 = $[104];
  }
  let t44;
  if ($[105] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = classes(shelf({
      columnGap: "1 | h3",
      justifyContent: "start"
    }));
    $[105] = t44;
  } else {
    t44 = $[105];
  }
  let t45;
  if ($[106] !== previousQuestion || $[107] !== question.id || $[108] !== question.title || $[109] !== questionIndexState.index || $[110] !== setQuestionIndex) {
    t45 = questionIndexState.index > 0 && <SecondaryButton onClick={() => {
      if (previousQuestion != null) {
        pushPreviousStepEvent({
          questionId: question.id,
          questionTitle: question.title
        });
      }
      setQuestionIndex({
        index: Math.max(questionIndexState.index - 1, 0),
        cause: "edit"
      });
    }} {...addTestSelector("uiPreviousQuestionButton")}>{translateInline({
        en: "Previous question",
        nl: "Vorige vraag"
      })}</SecondaryButton>;
    $[106] = previousQuestion;
    $[107] = question.id;
    $[108] = question.title;
    $[109] = questionIndexState.index;
    $[110] = setQuestionIndex;
    $[111] = t45;
  } else {
    t45 = $[111];
  }
  let t46;
  if ($[112] !== allQuestionsAnswered || $[113] !== firstUnansweredQuestion || $[114] !== givenAnswer || $[115] !== question.required || $[116] !== setGivenAnswer) {
    t46 = !question.required && givenAnswer == null ? <button type="submit" className={classes(callToActionBox({}))} onClick={() => {
      setGivenAnswer("");
    }} {...addTestSelector("uiSkipQuestionButton")}><span className={classes(textBlock({}))}>{translateInline({
          en: "Skip",
          nl: "Overslaan"
        })}</span></button> : <button type="submit" className={classes(callToActionBox({}), firstUnansweredQuestion && disabledButton)} {...addTestSelector("uiNextQuestionButton")}><span className={classes(textBlock({
        color: firstUnansweredQuestion ? "positive:noHover" : "inherit"
      }))}>{allQuestionsAnswered ? translateInline({
          en: "Go to results",
          nl: "Naar resultaten"
        }) : translateInline({
          en: "Next question",
          nl: "Volgende vraag"
        })}</span></button>;
    $[112] = allQuestionsAnswered;
    $[113] = firstUnansweredQuestion;
    $[114] = givenAnswer;
    $[115] = question.required;
    $[116] = setGivenAnswer;
    $[117] = t46;
  } else {
    t46 = $[117];
  }
  let t47;
  if ($[118] !== t44 || $[119] !== t45 || $[120] !== t46) {
    t47 = <div className={t44}>{t45}{t46}</div>;
    $[118] = t44;
    $[119] = t45;
    $[120] = t46;
    $[121] = t47;
  } else {
    t47 = $[121];
  }
  let t48;
  if ($[122] !== t3 || $[123] !== t43 || $[124] !== t47) {
    t48 = <div><Form onSubmit={t3}>{t43}{t47}</Form></div>;
    $[122] = t3;
    $[123] = t43;
    $[124] = t47;
    $[125] = t48;
  } else {
    t48 = $[125];
  }
  return t48;
};
function _temp(v) {
  return v !== null;
}
function _temp2(answer) {
  return answer === null;
}